import { useEffect, useState } from "react";
import { api } from "../../lib/api";

type Tab = "readings" | "activity" | "pain";

const SOURCE_LABEL: Record<string, string> = {
  whoop: "Whoop",
  oura: "Oura",
  google_health: "Google Health",
  apple_health: "Apple Health",
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
const num = (v: any, digits = 0) => (typeof v === "number" ? v.toFixed(digits) : "—");

export function HistoryScreen() {
  const [tab, setTab] = useState<Tab>("readings");
  const [readings, setReadings] = useState<any[] | null>(null);
  const [activity, setActivity] = useState<any[] | null>(null);
  const [pain, setPain] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load =
      tab === "readings"
        ? api.history.readings(range).then((r) => { if (!cancelled) setReadings(r.readings); })
        : tab === "activity"
        ? api.history.activity(Math.max(range, 60)).then((r) => { if (!cancelled) setActivity(r.activity); })
        : api.pain.history(Math.max(range, 90)).then((r) => { if (!cancelled) setPain(r.incidents); });
    load.catch(() => {/* surfaced as empty state */}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, range]);

  return (
    <div className="h-full flex flex-col min-h-0 p-3 sm:p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h1 className="font-serif text-[22px] font-semibold tracking-[0.01em]">History</h1>
        <div className="flex items-center gap-1 bg-d_card2/60 border border-d_border/20 rounded-lg p-0.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors ${
                range === d ? "bg-d_accent text-white" : "text-d_text2 hover:text-d_text"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 border-b border-d_border/20">
        {([["readings", "Device Readings"], ["activity", "Activity Log"], ["pain", "Pain"]] as Array<[Tab, string]>).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`text-[12px] font-semibold px-3 py-2 -mb-px border-b-2 transition-colors ${
              tab === key
                ? "border-d_accent text-d_accent"
                : "border-transparent text-d_text3 hover:text-d_text2"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto">
        {loading && (
          <div className="text-d_text3 text-xs tracking-wider uppercase py-8 text-center">Loading…</div>
        )}

        {!loading && tab === "readings" && (
          <ReadingsTable rows={readings ?? []} />
        )}

        {!loading && tab === "activity" && (
          <ActivityList items={activity ?? []} />
        )}

        {!loading && tab === "pain" && (
          <PainList items={pain ?? []} />
        )}
      </div>
    </div>
  );
}

function ReadingsTable({ rows }: { rows: any[] }) {
  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-d_text3">
        <div className="text-[13px] text-d_text2 font-medium mb-1">No device readings yet</div>
        <div className="text-[11px]">Connect a wearable and sync to build your history.</div>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr className="text-d_text3 text-[10px] uppercase tracking-[0.08em]">
            <th className="text-left font-semibold py-2 px-2 sticky top-0 bg-d_card">Date</th>
            <th className="text-left font-semibold py-2 px-2 sticky top-0 bg-d_card">Source</th>
            <th className="text-right font-semibold py-2 px-2 sticky top-0 bg-d_card">Recovery</th>
            <th className="text-right font-semibold py-2 px-2 sticky top-0 bg-d_card">HRV</th>
            <th className="text-right font-semibold py-2 px-2 sticky top-0 bg-d_card">RHR</th>
            <th className="text-right font-semibold py-2 px-2 sticky top-0 bg-d_card">Sleep</th>
            <th className="text-right font-semibold py-2 px-2 sticky top-0 bg-d_card">Sleep&nbsp;Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.date}-${r.source}-${i}`} className="border-t border-d_border/15 hover:bg-d_accent/[.03]">
              <td className="py-2 px-2 text-d_text font-medium whitespace-nowrap">{fmtDate(r.date)}</td>
              <td className="py-2 px-2 text-d_text2">{SOURCE_LABEL[r.source] ?? r.source}</td>
              <td className="py-2 px-2 text-right tabular-nums text-d_text">{num(r.recovery_score ?? r.readiness_score)}</td>
              <td className="py-2 px-2 text-right tabular-nums text-d_text">{num(r.hrv_avg)}<span className="text-d_text3 text-[10px]">ms</span></td>
              <td className="py-2 px-2 text-right tabular-nums text-d_text">{num(r.resting_hr)}</td>
              <td className="py-2 px-2 text-right tabular-nums text-d_text">{num(r.sleep_duration_hr, 1)}<span className="text-d_text3 text-[10px]">h</span></td>
              <td className="py-2 px-2 text-right tabular-nums text-d_text">{num(r.sleep_score)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityList({ items }: { items: any[] }) {
  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-d_text3">
        <div className="text-[13px] text-d_text2 font-medium mb-1">No activity yet</div>
        <div className="text-[11px]">Your sessions, check-ins, and journal entries will appear here.</div>
      </div>
    );
  }

  const meta: Record<string, { icon: string; color: string; label: string }> = {
    session: { icon: "🧘", color: "#2A9D8F", label: "Session" },
    checkin: { icon: "📝", color: "#457B9D", label: "Check-in" },
    journal: { icon: "📖", color: "#F4A261", label: "Journal" },
  };

  return (
    <div className="space-y-1.5">
      {items.map((it) => {
        const m = meta[it.kind] ?? meta.session;
        return (
          <div key={it.id} className="flex items-start gap-3 rounded-lg border border-d_border/20 bg-d_card2/40 px-3 py-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0" style={{ background: `${m.color}1a` }}>
              {m.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9px] tracking-[1.5px] uppercase font-bold" style={{ color: m.color }}>{m.label}</span>
                <span className="text-[10px] text-d_text3 whitespace-nowrap">{fmtDateTime(it.at)}</span>
              </div>
              <ActivityBody kind={it.kind} data={it.data} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PainList({ items }: { items: any[] }) {
  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-d_text3">
        <div className="text-[13px] text-d_text2 font-medium mb-1">No pain logged</div>
        <div className="text-[11px]">Mark pain points on the LifeScribe Body card and they'll appear here.</div>
      </div>
    );
  }

  const levelColor = (level: number) =>
    level <= 3 ? "#2A9D8F" : level <= 6 ? "#F4A261" : "#E76F51";
  const levelLabel = (level: number) =>
    level <= 3 ? "Mild" : level <= 6 ? "Moderate" : "Severe";

  // Group incidents by local day for a clean timeline.
  const groups: Record<string, any[]> = {};
  for (const it of items) {
    const day = fmtDate(it.recorded_at);
    (groups[day] = groups[day] || []).push(it);
  }

  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([day, incidents]) => (
        <div key={day}>
          <div className="text-[10px] tracking-[1.5px] uppercase text-d_text3 font-semibold mb-1.5">{day}</div>
          <div className="space-y-1.5">
            {incidents.map((it) => {
              const color = levelColor(it.level);
              const resolved = !!it.resolved_at;
              return (
                <div key={it.id} className="flex items-start gap-3 rounded-lg border border-d_border/20 bg-d_card2/40 px-3 py-2.5">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[13px]"
                    style={{ background: color }}
                  >
                    {it.level}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] tracking-[1.5px] uppercase font-bold" style={{ color }}>
                        {levelLabel(it.level)} · {it.level}/10
                      </span>
                      <span className="text-[10px] text-d_text3 whitespace-nowrap">{fmtTime(it.recorded_at)}</span>
                    </div>
                    <div className="text-[13px] text-d_text mt-0.5">
                      {it.description || (it.region ? it.region : "Pain point")}
                    </div>
                    <div className="text-[10px] text-d_text3 mt-0.5">
                      {resolved ? `Cleared ${fmtTime(it.resolved_at)}` : "Active when logged"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityBody({ kind, data }: { kind: string; data: any }) {
  if (kind === "session") {
    return (
      <div className="mt-0.5">
        <div className="text-[13px] text-d_text font-medium">{data.session_name ?? data.session_category ?? "Session"}</div>
        <div className="text-[11px] text-d_text3">
          {[data.practitioner_name, data.duration_minutes ? `${data.duration_minutes} min` : null, data.is_group_class ? "Group" : null]
            .filter(Boolean).join(" · ")}
        </div>
      </div>
    );
  }
  if (kind === "checkin") {
    const chips = [
      data.mood != null ? `Mood ${data.mood}` : null,
      data.energy != null ? `Energy ${data.energy}` : null,
      data.stress != null ? `Stress ${data.stress}` : null,
      data.sleep_quality != null ? `Sleep ${data.sleep_quality}` : null,
    ].filter(Boolean);
    return (
      <div className="mt-0.5">
        <div className="flex flex-wrap gap-1 mt-0.5">
          {chips.map((c, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-d_text/[.06] text-d_text2">{c}</span>
          ))}
        </div>
        {data.note && <div className="text-[11px] text-d_text3 mt-1 italic">"{data.note}"</div>}
      </div>
    );
  }
  // journal
  return (
    <div className="mt-0.5">
      <div className="text-[12px] text-d_text2">{data.word_count ?? 0} words logged</div>
      {Array.isArray(data.tags) && data.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {data.tags.map((t: string) => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-d_text/[.06] text-d_text2">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}
